import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./BroBizeeOnboarding.css";
import AppPermissions from "./AppPermissions";

const slidesData = [
  {
    id: 1,
    title: "Hello! Welcome To BroBizee",
    subtitle: "On Time. Online. On Demand. For You ♡",
    image: "/images/slide1.png",
    tagline: "Every Solution You Need, Comes With BroBizee.",
  },
  {
    id: 2,
    title: "Hello! Welcome To BroBizee",
    subtitle: "Connecting Skills, Simplifying Life. ♡",
    image: "/images/slide2.png",
    tagline: "Every Job You Need, Finds BroBizee.",
  },
  {
    id: 3,
    title: "Hello! Welcome To BroBizee",
    subtitle:
      "Pay Securely & Conveniently With BroBizee Secured Payment Option. ♡",
    image: "/images/slide3.png",
    tagline: "Every Service You Need, Meets BroBizee.",
  },
  {
    id: 4,
    title: "Hello! Welcome To BroBizee",
    subtitle: "Smart Support, One Click Away. ♡",
    image: "/images/slide4.png",
    tagline: "Every Solution You Need, Comes With BroBizee.",
  },
];

export default function BroBizeeOnboarding() {
  const navigate = useNavigate();
  const [showNetworkError, setShowNetworkError] = useState(false); 

  // =====================================================
  // ONBOARDING STATE
  // =====================================================

  const [activeSlide, setActiveSlide] = useState(0);

  const [loading, setLoading] = useState(false);

  // =====================================================
  // PERMISSION POPUP
  // =====================================================

  const [showPermissions, setShowPermissions] = useState(false);

  const [permissionLoading, setPermissionLoading] = useState(false);

  const [permissions, setPermissions] = useState({
    location: "unknown",
    notification: "unknown",
    camera: "unknown",
    microphone: "unknown",
    contacts: "unknown",
  });

  // =====================================================
  // GET / CREATE DEVICE UUID
  // =====================================================

  const getDeviceUUID = () => {
    let deviceUUID = localStorage.getItem("device_uuid");

    if (!deviceUUID) {
      if (
        typeof crypto !== "undefined" &&
        typeof crypto.randomUUID === "function"
      ) {
        deviceUUID = crypto.randomUUID();
      } else {
        deviceUUID =
          "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
            /[xy]/g,
            (c) => {
              const r = (Math.random() * 16) | 0;

              const v =
                c === "x"
                  ? r
                  : (r & 0x3) | 0x8;

              return v.toString(16);
            }
          );
      }

      localStorage.setItem(
        "device_uuid",
        deviceUUID
      );
    }

    return deviceUUID;
  };

  // =====================================================
  // CHECK INITIAL NOTIFICATION PERMISSION
  // =====================================================

  useEffect(() => {
    if ("Notification" in window) {
      setPermissions((prev) => ({
        ...prev,
        notification: Notification.permission,
      }));
    }
  }, []);

  // =====================================================
  // LOCATION PERMISSION
  // =====================================================

  const requestLocationPermission = () => {
    if (!navigator.geolocation) {
      setPermissions((prev) => ({
        ...prev,
        location: "unsupported",
      }));

      return;
    }

    setPermissions((prev) => ({
      ...prev,
      location: "requesting",
    }));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log(
          "Latitude:",
          position.coords.latitude
        );

        console.log(
          "Longitude:",
          position.coords.longitude
        );

        // Optional: save coordinates
        localStorage.setItem(
          "latitude",
          String(position.coords.latitude)
        );

        localStorage.setItem(
          "longitude",
          String(position.coords.longitude)
        );

        setPermissions((prev) => ({
          ...prev,
          location: "granted",
        }));
      },

      (error) => {
        console.log(
          "Location permission error:",
          error
        );

        setPermissions((prev) => ({
          ...prev,
          location:
            error.code === 1
              ? "denied"
              : "error",
        }));
      },

      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  // =====================================================
  // NOTIFICATION PERMISSION
  // =====================================================

  const requestNotificationPermission =
    async () => {
      if (!("Notification" in window)) {
        setPermissions((prev) => ({
          ...prev,
          notification: "unsupported",
        }));

        return;
      }

      try {
        setPermissions((prev) => ({
          ...prev,
          notification: "requesting",
        }));

        const result =
          await Notification.requestPermission();

        setPermissions((prev) => ({
          ...prev,
          notification: result,
        }));
      } catch (error) {
        console.error(
          "Notification permission error:",
          error
        );

        setPermissions((prev) => ({
          ...prev,
          notification: "error",
        }));
      }
    };

  // =====================================================
  // CAMERA PERMISSION
  // =====================================================

  const requestCameraPermission =
    async () => {
      if (
        !navigator.mediaDevices ||
        !navigator.mediaDevices.getUserMedia
      ) {
        setPermissions((prev) => ({
          ...prev,
          camera: "unsupported",
        }));

        return;
      }

      try {
        setPermissions((prev) => ({
          ...prev,
          camera: "requesting",
        }));

        const stream =
          await navigator.mediaDevices.getUserMedia(
            {
              video: true,
            }
          );

        stream
          .getTracks()
          .forEach((track) => track.stop());

        setPermissions((prev) => ({
          ...prev,
          camera: "granted",
        }));
      } catch (error) {
        console.error(
          "Camera permission error:",
          error
        );

        setPermissions((prev) => ({
          ...prev,
          camera:
            error.name ===
            "NotAllowedError"
              ? "denied"
              : "error",
        }));
      }
    };

  // =====================================================
  // MICROPHONE PERMISSION
  // =====================================================

  const requestMicrophonePermission =
    async () => {
      if (
        !navigator.mediaDevices ||
        !navigator.mediaDevices.getUserMedia
      ) {
        setPermissions((prev) => ({
          ...prev,
          microphone: "unsupported",
        }));

        return;
      }

      try {
        setPermissions((prev) => ({
          ...prev,
          microphone: "requesting",
        }));

        const stream =
          await navigator.mediaDevices.getUserMedia(
            {
              audio: true,
            }
          );

        stream
          .getTracks()
          .forEach((track) => track.stop());

        setPermissions((prev) => ({
          ...prev,
          microphone: "granted",
        }));
      } catch (error) {
        console.error(
          "Microphone permission error:",
          error
        );

        setPermissions((prev) => ({
          ...prev,
          microphone:
            error.name ===
            "NotAllowedError"
              ? "denied"
              : "error",
        }));
      }
    };

  // =====================================================
  // CONTACTS PERMISSION
  // =====================================================

  const requestContacts = async () => {
    if (!("contacts" in navigator)) {
      setPermissions((prev) => ({
        ...prev,
        contacts: "unsupported",
      }));

      console.log(
        "Contacts API is not supported."
      );

      return;
    }

    try {
      setPermissions((prev) => ({
        ...prev,
        contacts: "requesting",
      }));

      const contacts =
        await navigator.contacts.select(
          ["name", "tel", "email"],
          {
            multiple: true,
          }
        );

      console.log(
        "Selected contacts:",
        contacts
      );

      setPermissions((prev) => ({
        ...prev,
        contacts: "granted",
      }));
    } catch (error) {
      console.log(
        "Contacts cancelled:",
        error
      );

      setPermissions((prev) => ({
        ...prev,
        contacts: "denied",
      }));
    }
  };

  // =====================================================
  // REQUEST ALL PERMISSIONS
  // =====================================================

  const requestAllPermissions =
    async () => {
      if (permissionLoading) {
        return;
      }

      setPermissionLoading(true);

      try {
        // Location
        await new Promise((resolve) => {
          if (!navigator.geolocation) {
            setPermissions((prev) => ({
              ...prev,
              location: "unsupported",
            }));

            resolve();
            return;
          }

          navigator.geolocation.getCurrentPosition(
            (position) => {
              localStorage.setItem(
                "latitude",
                String(
                  position.coords.latitude
                )
              );

              localStorage.setItem(
                "longitude",
                String(
                  position.coords.longitude
                )
              );

              setPermissions((prev) => ({
                ...prev,
                location: "granted",
              }));

              resolve();
            },

            (error) => {
              setPermissions((prev) => ({
                ...prev,
                location:
                  error.code === 1
                    ? "denied"
                    : "error",
              }));

              resolve();
            },

            {
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 0,
            }
          );
        });

        // Notification
        if ("Notification" in window) {
          try {
            const result =
              await Notification.requestPermission();

            setPermissions((prev) => ({
              ...prev,
              notification: result,
            }));
          } catch (error) {
            console.error(error);
          }
        } else {
          setPermissions((prev) => ({
            ...prev,
            notification: "unsupported",
          }));
        }

        // Camera
        if (
          navigator.mediaDevices &&
          navigator.mediaDevices.getUserMedia
        ) {
          try {
            const stream =
              await navigator.mediaDevices.getUserMedia(
                {
                  video: true,
                }
              );

            stream
              .getTracks()
              .forEach((track) =>
                track.stop()
              );

            setPermissions((prev) => ({
              ...prev,
              camera: "granted",
            }));
          } catch (error) {
            console.error(
              "Camera:",
              error
            );

            setPermissions((prev) => ({
              ...prev,
              camera:
                error.name ===
                "NotAllowedError"
                  ? "denied"
                  : "error",
            }));
          }
        } else {
          setPermissions((prev) => ({
            ...prev,
            camera: "unsupported",
          }));
        }

        // Microphone
        if (
          navigator.mediaDevices &&
          navigator.mediaDevices.getUserMedia
        ) {
          try {
            const stream =
              await navigator.mediaDevices.getUserMedia(
                {
                  audio: true,
                }
              );

            stream
              .getTracks()
              .forEach((track) =>
                track.stop()
              );

            setPermissions((prev) => ({
              ...prev,
              microphone: "granted",
            }));
          } catch (error) {
            console.error(
              "Microphone:",
              error
            );

            setPermissions((prev) => ({
              ...prev,
              microphone:
                error.name ===
                "NotAllowedError"
                  ? "denied"
                  : "error",
            }));
          }
        } else {
          setPermissions((prev) => ({
            ...prev,
            microphone: "unsupported",
          }));
        }

        // Contacts
        if ("contacts" in navigator) {
          try {
            const contacts =
              await navigator.contacts.select(
                ["name", "tel", "email"],
                {
                  multiple: true,
                }
              );

            console.log(
              "Contacts:",
              contacts
            );

            setPermissions((prev) => ({
              ...prev,
              contacts: "granted",
            }));
          } catch (error) {
            console.log(
              "Contacts:",
              error
            );

            setPermissions((prev) => ({
              ...prev,
              contacts: "denied",
            }));
          }
        } else {
          setPermissions((prev) => ({
            ...prev,
            contacts: "unsupported",
          }));
        }
      } finally {
        setPermissionLoading(false);
      }
    };

  // =====================================================
  // PERMISSION STATUS TEXT
  // =====================================================

  const getPermissionText = (
    permission
  ) => {
    switch (permission) {
      case "granted":
        return "Allowed";

      case "denied":
        return "Denied";

      case "unsupported":
        return "Unavailable";

      case "requesting":
        return "Please wait...";

      case "error":
        return "Try Again";

      default:
        return "Allow";
    }
  };

  // =====================================================
  // PERMISSION BUTTON DISABLED
  // =====================================================

  const isPermissionGranted = (
    permission
  ) => {
    return permission === "granted";
  };

  // =====================================================
  // CONTINUE AFTER PERMISSIONS
  // =====================================================

  const handlePermissionContinue =
    () => {
      setShowPermissions(false);

      navigate("/language");
    };

  // =====================================================
  // GET STARTED
  // =====================================================

  const handleNext = async () => {
    // ---------------------------------------------------
    // NEXT SLIDE
    // ---------------------------------------------------

    if (
      activeSlide <
      slidesData.length - 1
    ) {
      setActiveSlide(
        (prev) => prev + 1
      );

      return;
    }

    // ---------------------------------------------------
    // PREVENT DOUBLE CLICK
    // ---------------------------------------------------

    if (loading) {
      return;
    }

    setLoading(true);

    try {
      // -------------------------------------------------
      // DEVICE UUID
      // -------------------------------------------------

      const deviceUUID =
        getDeviceUUID();

      console.log(
        "Device UUID:",
        deviceUUID
      );

      // -------------------------------------------------
      // API REQUEST
      // -------------------------------------------------

      const response = await fetch(
        "https://coinyoco.world/brobizee-api/register.php",
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",
          },

          body: JSON.stringify({
            device_uuid:
              deviceUUID,
          }),
        }
      );

      console.log(
        "HTTP Status:",
        response.status
      );

      // -------------------------------------------------
      // HTTP ERROR
      // -------------------------------------------------

      if (!response.ok) {
        throw new Error(
          `HTTP ${response.status}`
        );
      }

      // -------------------------------------------------
      // JSON
      // -------------------------------------------------

      const result =
        await response.json();

      console.log(
        "API Response:",
        result
      );

      // -------------------------------------------------
      // SUCCESS
      // -------------------------------------------------

      if (result.success) {
        // Save UUID
        localStorage.setItem(
          "device_uuid",
          result.device_uuid ||
            deviceUUID
        );

        // Save user ID
        if (
          result.user_id !==
            undefined &&
          result.user_id !== null
        ) {
          localStorage.setItem(
            "user_id",
            String(result.user_id)
          );
        }

        // ------------------------------------------------
        // SHOW PERMISSION POPUP
        // ------------------------------------------------

        setShowPermissions(true);
      } else {
        alert(
          result.message ||
            "Device registration failed."
        );
      }
    } catch (error) {
      console.error(
        "API ERROR:",
        error
      );

      setShowNetworkError(true);

      // alert(

        
      //   "Server se connection nahi ho raha.\n\n" +
      //     "Check karo:\n" +
      //     "1. XAMPP Apache ON hai?\n" +
      //     "2. PHP API path correct hai?\n" +
      //     "3. register.php browser me open ho raha hai?\n" +
      //     "4. React localhost:3000 par chal raha hai?"
      // );

    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // DOT CLICK
  // =====================================================

  const handleDotClick = (
    index
  ) => {
    if (loading) {
      return;
    }

    setActiveSlide(index);
  };

  // =====================================================
  // UI
  // =====================================================

  return (
    <>
      {/* =================================================
          ONBOARDING
      ================================================= */}

      <div className="page-wrapper">
        <div className="app-card">
          {/* HEADER */}

          <header className="header">
            <img
              src="/images/logo.png"
              alt="BroBizee Logo"
              className="logo-img"
            />
          </header>

          {/* CONTENT */}

          <main className="content-container">
            <div
              className="slides-track"
              style={{
                transform:
                  `translateX(-${
                    activeSlide * 100
                  }%)`,
              }}
            >
              {slidesData.map(
                (slide) => (
                  <div
                    key={slide.id}
                    className="slide"
                  >
                    <div className="text-group">
                      <h2 className="title">
                        {slide.title}
                      </h2>

                      <p className="subtitle">
                        {
                          slide.subtitle
                        }
                      </p>
                    </div>

                    <div className="illustration-wrapper">
                      <img
                        src={
                          slide.image
                        }
                        alt={`Illustration ${slide.id}`}
                        className="illustration-img"
                      />
                    </div>

                    <p className="tagline">
                      {
                        slide.tagline
                      }
                    </p>
                  </div>
                )
              )}
            </div>
          </main>

          {/* FOOTER */}

          <footer className="footer">
            {/* PAGINATION */}

            <div className="pagination">
              {slidesData.map(
                (_, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`dot ${
                      activeSlide ===
                      index
                        ? "active"
                        : ""
                    }`}
                    onClick={() =>
                      handleDotClick(
                        index
                      )
                    }
                    disabled={loading}
                  />
                )
              )}
            </div>

            {/* BUTTON */}

            <button
              type="button"
              className="primary-btn"
              onClick={
                handleNext
              }
              disabled={loading}
            >
              {loading
                ? "Please Wait..."
                : activeSlide ===
                    slidesData.length -
                      1
                  ? "Get Started"
                  : "Continue & Next"}
            </button>

            {/* TERMS */}

            <p className="terms-text">
              By Continuing, You Agree
              To Our
              <br />

              <a href="#terms">
                Terms & Conditions
              </a>

              {" | "}

              <a href="#privacy">
                Privacy Policy
              </a>

              {" | "}

              <a href="#content">
                Content Policy
              </a>
            </p>
          </footer>
        </div>
      </div>

      {/* =================================================
          PERMISSION POPUP
      ================================================= */}

      {showPermissions && (
        <div
          className="permission-overlay"
          onClick={(e) => {
            // Popup ke bahar click karne par close nahi hoga
            e.stopPropagation();
          }}
        >
          <div
            className="permission-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >
            {/* HEADER */}

            <div className="permission-header">
              <div className="permission-logo">
                <img
                  src="/images/logo.png"
                  alt="BroBizee"
                />
              </div>

              <h2>
                BroBizee Permissions
              </h2>

              <p>
                BroBizee needs a few permissions so that the app's location, calling, media, contacts, and notification features can work properly.
              </p>
            </div>

            {/* =================================================
                LOCATION
            ================================================= */}

            <div className="permission-row">
              <div className="permission-icon">
                📍
              </div>

              <div className="permission-info">
                <strong>
                  Location
                </strong>

                <small>
                 To find nearby workers and services
                </small>
              </div>

              <button
                type="button"
                className={
                  isPermissionGranted(
                    permissions.location
                  )
                    ? "permission-allowed"
                    : "permission-allow"
                }
                onClick={
                  requestLocationPermission
                }
                disabled={
                  permissions.location ===
                  "requesting"
                }
              >
                {getPermissionText(
                  permissions.location
                )}
              </button>
            </div>

            {/* =================================================
                NOTIFICATION
            ================================================= */}

            <div className="permission-row">
              <div className="permission-icon">
                🔔
              </div>

              <div className="permission-info">
                <strong>
                  Notifications
                </strong>

                <small>
                 To receive booking and service updates.
                </small>
              </div>

              <button
                type="button"
                className={
                  isPermissionGranted(
                    permissions.notification
                  )
                    ? "permission-allowed"
                    : "permission-allow"
                }
                onClick={
                  requestNotificationPermission
                }
                disabled={
                  permissions.notification ===
                  "requesting"
                }
              >
                {getPermissionText(
                  permissions.notification
                )}
              </button>
            </div>

            {/* =================================================
                CAMERA
            ================================================= */}

            <div className="permission-row">
              <div className="permission-icon">
                📷
              </div>

              <div className="permission-info">
                <strong>
                  Camera
                </strong>

                <small>
                 To upload a profile photo and verify your identity.
                </small>
              </div>

              <button
                type="button"
                className={
                  isPermissionGranted(
                    permissions.camera
                  )
                    ? "permission-allowed"
                    : "permission-allow"
                }
                onClick={
                  requestCameraPermission
                }
                disabled={
                  permissions.camera ===
                  "requesting"
                }
              >
                {getPermissionText(
                  permissions.camera
                )}
              </button>
            </div>

            {/* =================================================
                MICROPHONE / CALLING
            ================================================= */}

            <div className="permission-row">
              <div className="permission-icon">
                🎤
              </div>

              <div className="permission-info">
                <strong>
                  Microphone / Calling
                </strong>

                <small>
                  To enable voice and calling features.
                </small>
              </div>

              <button
                type="button"
                className={
                  isPermissionGranted(
                    permissions.microphone
                  )
                    ? "permission-allowed"
                    : "permission-allow"
                }
                onClick={
                  requestMicrophonePermission
                }
                disabled={
                  permissions.microphone ===
                  "requesting"
                }
              >
                {getPermissionText(
                  permissions.microphone
                )}
              </button>
            </div>

            {/* =================================================
                CONTACTS
            ================================================= */}

            <div className="permission-row">
              <div className="permission-icon">
                👥
              </div>

              <div className="permission-info">
                <strong>
                  Contacts
                </strong>

                <small>
                  To select and share contacts.
                </small>
              </div>

              <button
                type="button"
                className={
                  isPermissionGranted(
                    permissions.contacts
                  )
                    ? "permission-allowed"
                    : "permission-allow"
                }
                onClick={
                  requestContacts
                }
                disabled={
                  permissions.contacts ===
                  "requesting"
                }
              >
                {getPermissionText(
                  permissions.contacts
                )}
              </button>
            </div>

            {/* =================================================
                ALLOW ALL
            ================================================= */}

            <button
              type="button"
              className="allow-all-button"
              onClick={
                requestAllPermissions
              }
              disabled={
                permissionLoading
              }
            >
              {permissionLoading
                ? "Requesting Permissions..."
                : "Allow Available Permissions"}
            </button>

            {/* =================================================
                CONTINUE
            ================================================= */}

            <button
              type="button"
              className="permission-continue-button"
              onClick={
                handlePermissionContinue
              }
            >
              Continue
            </button>

            <p className="permission-note">
             You can change these denied permissions later in your browser settings.
            </p>
          </div>
        </div>
      )}

      {showNetworkError && (
        <div className="network-screen-wrapper">

   
           <div className="network-modal-card">

        {/* Header */}
        <div className="network-header">

          <div className="network-icon">
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#005CA9"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />

              <line
                x1="9"
                y1="9"
                x2="9.01"
                y2="9"
                strokeWidth="3"
              />

              <line
                x1="15"
                y1="9"
                x2="15.01"
                y2="9"
                strokeWidth="3"
              />

              <line
                x1="9"
                y1="15"
                x2="15"
                y2="15"
              />
            </svg>
          </div>

          <h2 className="network-title">
            Network Error
          </h2>

        </div>

        {/* Message */}
        <p className="network-message">
          We’re unable to connect to the server right now.Please Check Your Internet Connection
          And Try Again.
        </p>

        {/* OK */}
        <button
          type="button"
          className="network-ok-btn"
          onClick={() => {
            setShowNetworkError(false);
          }}
        >
          OK
        </button>

          </div>

   
         </div>
      )} 

      <style>
  {`
        /* popup CSS */.network-screen-wrapper {
  position: fixed;
  inset: 0;
  z-index: 99999;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 10px;
  box-sizing: border-box;

  background: rgba(0, 0, 0, 0.28);

  backdrop-filter: blur(7px);
  -webkit-backdrop-filter: blur(7px);

  font-family:
    -apple-system,
    BlinkMacSystemFont,
    "SF Pro Text",
    "Segoe UI",
    Roboto,
    sans-serif;
}

.network-device-frame {
  width: 100%;
  max-width: 410px;

  border-radius: 32px;

  padding: 30px 0;

  display: flex;
  justify-content: center;
  align-items: center;

  box-sizing: border-box;

  box-shadow:
    0 30px 70px rgba(0, 0, 0, 0.20),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);

  animation: networkPopup 0.28s
    cubic-bezier(0.16, 1, 0.3, 1);
}

.network-modal-card {
  width: 88%;
  max-width: 370px;

  background: rgba(255, 255, 255, 0.98);

  border-radius: 20px;

  padding: 28px 22px;

  text-align: center;

  box-sizing: border-box;

  box-shadow:
    0 12px 35px rgba(0, 0, 0, 0.14);
}

.network-header {
  display: flex;
  justify-content: center;
  align-items: center;

  gap: 10px;

  margin-bottom: 14px;
}

.network-icon {
  width: 30px;
  height: 30px;

  display: flex;
  align-items: center;
  justify-content: center;
}

.network-title {
  margin: 0;

  font-size: 20px;
  font-weight: 700;

  color: #111;
}

.network-message {
  margin: 0 auto 24px;

  max-width: 290px;

  font-size: 14px;
  line-height: 1.5;

  color: #555;
}

.network-ok-btn {
  width: 100%;
  height: 48px;

  border: none;
  border-radius: 12px;

  background: #005CA9;
  color: #FF6B00;

  font-size: 16px;
  font-weight: 700;

  cursor: pointer;

  transition: 0.2s ease;

  box-shadow:
    0 5px 14px rgba(0, 92, 169, 0.25);
}

.network-ok-btn:hover {
  background: #004C8C;
  transform: translateY(-1px);
}

.network-ok-btn:active {
  transform: scale(0.98);
}

@keyframes networkPopup {
  from {
    opacity: 0;
    transform: scale(0.92);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Mobile */
@media (max-width: 480px) {

  .network-screen-wrapper {
    padding: 16px;
  }

  .network-device-frame {
    max-width: 100%;
    border-radius: 28px;
    padding: 22px 0;
  }

  .network-modal-card {
    width: 90%;
    padding: 24px 18px;
  }

  .network-title {
    font-size: 18px;
  }

  .network-message {
    font-size: 13px;
  }

  .network-ok-btn {
    height: 45px;
    font-size: 15px;
  }
}

/* Tablet */
@media (min-width: 768px) {

  .network-device-frame {
    max-width: 440px;
  }
}
      `}
      </style> 


    </>
  );
}